/**
 * Citation-status predicates: is this citation a real citation, or a marker?
 *
 * Pure and DOM-free so they can be tested directly. They live in core rather
 * than beside the renderer because the conditions they detect are currently
 * DORMANT — every case in the corpus now carries a confirmed reporter citation,
 * so nothing exercises this code at runtime. A dormant guard with no test is a
 * guard that quietly stops working; these fire again the next time
 * `fetch:caselaw` pulls an opinion decided before its bound-volume citation
 * exists, which is routine for recent decisions.
 */

/**
 * An unpublished summary disposition. Mass. App. Ct. Rule 23.0 (formerly Rule
 * 1:28) decisions may be cited for persuasive value only, never as binding
 * precedent, so presenting one as ordinary authority is the worst failure this
 * corpus has. Keyed on the marker the capture writes into the citation.
 */
export function isUnpublished(citation: string | null | undefined): boolean {
  return /unpublished/i.test(citation || '');
}

/**
 * A case still carrying the capture-time placeholder instead of a citation:
 * "Mass. App. Ct. No. 23-P-496 (2024) — reporter citation & precedential status
 * not confirmed; verify against the official reporter".
 *
 * Deliberately separate from isUnpublished(). The placeholder says "not
 * confirmed", not "unpublished", so the unpublished test does NOT fire on it —
 * which is exactly how six of these once rendered as ordinary grey metadata
 * beside real citations, the entries most needing a flag looking the most
 * routine.
 */
export function isUnconfirmedCitation(citation: string | null | undefined): boolean {
  return /not confirmed/i.test(citation || '');
}

/**
 * What to SHOW for a citation. For a placeholder, the docket-number head only —
 * the prose tail is carried by the badge and banner instead, and repeating it
 * inline buries the case name. The STORED value always keeps the full caveat;
 * this affects display only.
 */
export function citationLabel(citation: string | null | undefined): string {
  const c = String(citation || '');
  return isUnconfirmedCitation(c) ? c.split(/\s+—\s+/)[0].trim() : c;
}
